import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTable, useSortBy, usePagination } from 'react-table';
import { FaSyncAlt, FaDownload, FaCopy, FaPlus, FaMinus } from 'react-icons/fa';
import LoadingImpulse from './LoadingImpulse';

const BankTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    phone: '',
    receipt: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.phone) params.append('phone', filters.phone.trim());
      if (filters.receipt) params.append('receipt', filters.receipt.trim());

      try {
        const response = await axios.get(
          `https://instant-transfer-back-production.up.railway.app/api/admin/bank-transactions?${params.toString()}`
        );
        const sorted = (response.data.transactions || []).sort(
          (a, b) => new Date(b.transactionDate) - new Date(a.transactionDate)
        );
        setTransactions(sorted);
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load bank transactions');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, [filters]);

  const columns = useMemo(
    () => [
      {
        Header: 'Date',
        id: 'date',
        accessor: 'transactionDate',
        Cell: ({ value }) => {
          if (!value) return 'N/A';
          const date = new Date(value);
          return date.toLocaleDateString('en-US', {
            timeZone: 'Africa/Nairobi',
            year: '2-digit',
            month: '2-digit',
            day: '2-digit'
          });
        }
      },
      {
        Header: 'Time',
        id: 'time',
        accessor: 'transactionDate',
        Cell: ({ value }) => {
          if (!value) return 'N/A';
          const date = new Date(value);
          return date.toLocaleTimeString('en-US', {
            timeZone: 'Africa/Nairobi',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => (
          <span
            className={`px-1.5 py-0.5 rounded-full text-[6px] font-sftsBold sm:text-xs ${
              value === 'completed'
                ? 'bg-green-100 text-green-800'
                : value === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : value === 'failed'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {value.toUpperCase()}
          </span>
        )
      },
      {
        Header: 'CR Acct',
        accessor: 'accountReference',
        Cell: ({ value }) => (
          <span className="  text-md text-back">{value}</span>
        )
      },
      {
        Header: 'Bank A/C',
        accessor: 'accountNumber',
        Cell: ({ value }) => value || '—'
      },
      {
        Header: 'Acc Name',
        accessor: 'accountName',
        Cell: ({ value }) => value || '—'
      },
      {
        Header: 'Receipt',
        accessor: 'receiptNumber',
        Cell: ({ value }) => value && value !== 'Pending' ? (
          <div className="flex items-center gap-1">
            <span className="font-sftsMedium text-[7px] sm:text-xs">{value}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(value);
              }}
              className="text-gray-600 hover:text-gray-600"
              title="Copy receipt"
            >
              <FaCopy size={10} />
            </button>
          </div>
        ) : (
          <span className="text-gray-600  text-[7px] sm:text-xs">Pending</span>
        )
      },
      {
        Header: 'KES',
        accessor: 'amountKES',
        Cell: ({ value }) => value ? (
          <strong className="text-[7px] sm:text-sm font-sftsBold">
            KES {Number(value).toLocaleString()}
          </strong>
        ) : '—'
      },
      {
        Header: 'USD',
        accessor: 'amountUSD',
        Cell: ({ value }) => <strong className="text-green-600 text-[7px] sm:text-sm">${value}</strong>
      }
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data: transactions,
      initialState: { pageSize: 20, sortBy: [{ id: 'date', desc: true }] }
    },
    useSortBy,
    usePagination
  );

  const toggleRowExpansion = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setError('');
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.phone) params.append('phone', filters.phone.trim());
    if (filters.receipt) params.append('receipt', filters.receipt.trim());

    axios.get(`https://instant-transfer-back-production.up.railway.app/api/admin/bank-transactions?${params.toString()}`)
      .then(res => {
        const sorted = (res.data.transactions || []).sort(
          (a, b) => new Date(b.transactionDate) - new Date(a.transactionDate)
        );
        setTransactions(sorted);
      })
      .catch(() => setError('Failed to refresh'))
      .finally(() => setIsLoading(false));
  };

  const handleExportCSV = () => {
    const headers = ['Date', 'Time', 'Status', 'CR Acct', 'Phone', 'Bank A/C', 'Acc Name', 'Receipt', 'KES', 'USD'];
    const rows = transactions.map(tx => {
      const date = new Date(tx.transactionDate);
      return [
        date.toLocaleDateString('en-US', { timeZone: 'Africa/Nairobi', year: '2-digit', month: '2-digit', day: '2-digit' }),
        date.toLocaleTimeString('en-US', { timeZone: 'Africa/Nairobi', hour: '2-digit', minute: '2-digit', hour12: false }),
        tx.status.toUpperCase(),
        tx.accountReference,
        tx.phoneNumber || '—',
        tx.accountNumber || '—',
        tx.accountName || '—',
        tx.receiptNumber && tx.receiptNumber !== 'Pending' ? tx.receiptNumber : 'Pending',
        tx.amountKES ? Number(tx.amountKES).toLocaleString() : '0',
        tx.amountUSD
      ];
    });

    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bank_withdrawals_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) return (
    <div className="min-h-screen bg-gray-100 py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4 flex items-center justify-center">
      <div className="bg-red-50 border-l-2 border-red-500 p-3 rounded-r shadow text-red-700 text-xs font-sftsMedium text-center">
        {error}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12">
        <LoadingImpulse size="lg" color="blue" />
      </div>
    );
  }

  if (!transactions.length) return (
    <div className="min-h-screen bg-gray-100 py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4 flex items-center justify-center">
      <div className="text-center text-gray-500">
        <span className="material-icons text-6xl text-gray-300 mb-2">account_balance_wallet</span>
        <p className="text-sm ">No bank withdrawal transactions found</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-2 px-1 sm:py-4 sm:px-2 md:py-6 md:px-4">
      <div className="max-w-full mx-auto sm:max-w-3xl md:max-w-6xl">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <h1 className="text-base  text-gray-800 sm:text-xl md:text-md font-sftsTitle">
            Bank Withdrawals 
          </h1>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleExportCSV}
              className="px-3 py-1 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 text-xs font-sftsMedium transition-all duration-200 flex items-center gap-1 sm:px-4 sm:py-2 sm:text-sm font-sftsMedium"
            >
              <FaDownload />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">Export</span>
            </button>
            <button
              onClick={handleRefresh}
              className="px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 text-xs font-sftsMedium transition-all duration-200 flex items-center gap-1 sm:px-4 sm:py-2 sm:text-sm font-sftsMedium"
            >
              <FaSyncAlt className={isLoading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
              <span className="sm:hidden">Refresh</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-col sm:flex-row gap-2">
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-sftsMedium"
          >
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>

          <input
            type="text"
            name="phone"
            placeholder="Search phone..."
            value={filters.phone}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-sftsMedium"
          />

          <input
            type="text"
            name="receipt"
            placeholder="Receipt number..."
            value={filters.receipt}
            onChange={handleFilterChange}
            className="px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-sftsMedium"
          />
        </div>

        {/* Mobile View - Card Layout */}
        {isMobile ? (
          <div className="space-y-3">
            {page.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.has(row.id);
              const rowData = row.original;

              return (
                <div key={row.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                  {/* Card Header */}
                  <div 
                    className="p-3 flex justify-between items-center cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleRowExpansion(row.id)}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-[8px] font-sftsBold ${
                          rowData.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : rowData.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : rowData.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {rowData.status.toUpperCase()}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {new Date(rowData.transactionDate).toLocaleTimeString('en-US', {
                            timeZone: 'Africa/Nairobi',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}
                        </span>
                      </div>
                      <div className="text-xs font-sftsBold text-gray-800">
                        KES {Number(rowData.amountKES).toLocaleString()}
                      </div>
                      <div className="text-[10px] text-gray-600">
                        Ref: {rowData.accountReference}
                      </div>
                    </div>
                    <button className="text-red-500">
                      {isExpanded ? <FaMinus size={12} /> : <FaPlus size={12} />}
                    </button>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-gray-100">
                      <div className="grid grid-cols-2 gap-2 pt-3 text-[10px]">
                        <div>
                          <div className="text-gray-500 font-sftsMedium mb-0.5">Date</div>
                          <div className="text-gray-800">
                            {new Date(rowData.transactionDate).toLocaleDateString('en-US', {
                              timeZone: 'Africa/Nairobi',
                              year: '2-digit',
                              month: '2-digit',
                              day: '2-digit'
                            })}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 font-sftsMedium mb-0.5">USD</div>
                          <div className="text-green-600 font-sftsBold">${rowData.amountUSD}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 font-sftsMedium mb-0.5">Phone</div>
                          <div className="flex items-center gap-1">
                            <span>{rowData.phoneNumber || '—'}</span>
                            {rowData.phoneNumber && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(rowData.phoneNumber);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <FaCopy size={8} />
                              </button>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-500 font-sftsMedium mb-0.5">Bank A/C</div>
                          <div className="text-gray-800">{rowData.accountNumber || '—'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 font-sftsMedium mb-0.5">Account Name</div>
                          <div className="text-gray-800">{rowData.accountName || '—'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500 font-sftsMedium mb-0.5">Receipt</div>
                          <div className="flex items-center gap-1">
                            <span className={rowData.receiptNumber && rowData.receiptNumber !== 'Pending' ? 'font-sftsMedium' : 'text-gray-400'}>
                              {rowData.receiptNumber && rowData.receiptNumber !== 'Pending' ? rowData.receiptNumber : 'Pending'}
                            </span>
                            {rowData.receiptNumber && rowData.receiptNumber !== 'Pending' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(rowData.receiptNumber);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <FaCopy size={8} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Desktop View - Table Layout */
          <div className="space-y-3">
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table {...getTableProps()} className="w-full">
                  <thead className="bg-gray-800 text-white">
                    {headerGroups.map(headerGroup => (
                      <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                        {headerGroup.headers.map(column => (
                          <th
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            className="px-3 py-3 text-left text-[10px] font-sftsBold uppercase tracking-wider cursor-pointer hover:bg-gray-700 transition font-sftsTitle sm:text-xs"
                            key={column.id}
                          >
                            {column.render('Header')}
                            <span className="ml-1">
                              {column.isSorted ? (column.isSortedDesc ? ' Down' : ' Up') : ''}
                            </span>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()} className="divide-y divide-gray-200">
                    {page.map(row => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()} key={row.id} className="hover:bg-gray-50 transition">
                          {row.cells.map(cell => (
                            <td
                              {...cell.getCellProps()}
                              className="px-3 py-4 text-sm text-gray-700 font-sftsMedium sm:text-sm truncate"
                              key={cell.column.id}
                            >
                              {cell.render('Cell')}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pageOptions.length > 1 && (
          <div className="mt-2 flex flex-col sm:flex-row justify-between items-center gap-1 sm:mt-3 sm:gap-2 md:mt-4 md:gap-3">
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="px-2 py-0.5 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 text-xs font-sftsMedium sm:px-3 sm:py-1 sm:text-sm md:px-4 md:py-2 md:text-base font-sftsMedium"
          >
            Previous
          </button>
          <span className="text-xs text-gray-600 sm:text-sm md:text-base font-sftsMedium">
            Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
          </span>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="px-2 py-0.5 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 text-xs font-sftsMedium sm:px-3 sm:py-1 sm:text-sm md:px-4 md:py-2 md:text-base font-sftsMedium"
          >
            Next
          </button>
        </div>
        )}
      </div>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600&family=Open+Sans:wght@400&display=swap');
      `}</style>
    </div>
  );
};

export default BankTransactions;